/**
 * Pure authorization helpers. Framework-agnostic and side-effect free
 * so they can be used in route guards, hooks, and plain logic alike.
 */
import { ROLE_LEVEL } from './roles'
import { ROLE_PERMISSIONS } from './permissions'
import type { UserRole } from './roles'
import type { Permission } from './permissions'

/**
 * Minimal structural shape an authenticated subject must satisfy to be
 * authorized. `AuthUser` (features/auth) is assignable to this — no
 * import cycle.
 */
export interface RbacSubject {
  /** Primary (highest-privilege) role — used for display, landing, and as the
   * fallback when `roles` is omitted. */
  role: UserRole
  /**
   * All RBAC roles the subject holds. Authorization considers the union of
   * these — a user may hold several (e.g. Admin + Compliance Officer), and a
   * super admin impersonating an account is granted both. Falls back to
   * `[role]` when omitted, so single-role subjects behave exactly as before.
   */
  roles?: ReadonlyArray<UserRole>
  /** Explicit grants that extend the role's defaults. */
  permissions?: ReadonlyArray<Permission>
}

export type CheckMode = 'all' | 'any'

/** The subject's effective role list — explicit `roles`, else just `[role]`. */
function subjectRoles(subject: RbacSubject): ReadonlyArray<UserRole> {
  return subject.roles && subject.roles.length > 0 ? subject.roles : [subject.role]
}

/** Effective permission set = union of every held role's defaults ∪ explicit grants. */
export function resolvePermissions(subject: RbacSubject): Set<Permission> {
  const set = new Set<Permission>()
  for (const role of subjectRoles(subject)) {
    ROLE_PERMISSIONS[role].forEach((p) => set.add(p))
  }
  subject.permissions?.forEach((p) => set.add(p))
  return set
}

/** Does the subject have a single permission? */
export function can(subject: RbacSubject, permission: Permission): boolean {
  return resolvePermissions(subject).has(permission)
}

/** Check several permissions at once ('any' = some, 'all' = every). */
export function canAccess(
  subject: RbacSubject,
  permissions: ReadonlyArray<Permission>,
  mode: CheckMode = 'any',
): boolean {
  if (permissions.length === 0) return true
  const granted = resolvePermissions(subject)
  return mode === 'all'
    ? permissions.every((p) => granted.has(p))
    : permissions.some((p) => granted.has(p))
}

/** Does the subject hold at least one of the allowed roles? */
export function hasRole(
  subject: RbacSubject,
  allowed: UserRole | ReadonlyArray<UserRole>,
): boolean {
  const list = Array.isArray(allowed) ? allowed : [allowed]
  return subjectRoles(subject).some((role) => list.includes(role))
}

/** Does any role the subject holds meet or exceed the minimum authority level? */
export function hasMinRole(subject: RbacSubject, min: UserRole): boolean {
  const minLevel = ROLE_LEVEL[min]
  return subjectRoles(subject).some((role) => ROLE_LEVEL[role] >= minLevel)
}
